<template>
  <el-card shadow="never" v-loading="loading">
    <div slot="header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ name: $root.getDashboardPath() }">Dashboard</el-breadcrumb-item>
        <el-breadcrumb-item v-if="$root.state.facility" :to="{ name: 'Facility Units' }">{{String($root.state.facilityData.code).toUpperCase()}} Units</el-breadcrumb-item>
        <el-breadcrumb-item v-if="$root.state.facility && unitData" :to="{ name: 'Facility Unit' }">{{String($root.state.facilityData.code).toUpperCase()}} {{ unitData.name }} Unit</el-breadcrumb-item>
      </el-breadcrumb>
    </div>
    <el-tabs v-model="activeTab" @tab-click="checkForPhotos">
      <el-tab-pane label="Info" name="info">
        <el-descriptions v-if="unitData">
          <template slot="extra">
            <el-button-group v-if="unitData.status !== statuses[1] || unitData.retired">
              <el-button
                size="small"
                icon="el-icon-picture"
                @click="openEditImagesDialog"
              >
                Update images
              </el-button>
              <el-button
                size="small"
                icon="el-icon-tickets"
                @click="openEditDescriptionDialog"
              >
                Update description
              </el-button>
              <el-button
                size="small"
                icon="el-icon-collection-tag"
                @click="openEditTagsDialog"
              >
                Update tags
              </el-button>
            </el-button-group>
            <!--
              Don't show a disabled Edit Unit button when the individual Update buttons are shown
            -->
            <el-button
              v-else
              size="small"
              icon="el-icon-edit"
              @click="editUnit"
            >
              Edit Unit
            </el-button>

            <el-button
              v-if="(unitData.status === statuses[1] || unitData.status === statuses[statuses.length - 1]) && !unitData.retired"
              type="danger"
              plain
              size="small"
              @click="openRetireUnitForm"
            >
              Retire Unit
            </el-button>
            <el-button
              v-if="unitData.status === statuses[1]"
              type="primary"
              size="small"
              @click="openRentForm"
              :disabled="unitData.retired"
            >
              New Lease
            </el-button>
            <el-button
              v-if="unitData.status !== 'PENDING' && invoice && invoice.end && !invoice.completed"
              type="success"
              size="small"
              @click="() => openCompleteLeaseDialog()"
            >
              Complete Lease
            </el-button>
            <el-dialog
             :visible.sync="completeLeaseDialog"
             destroy-on-close
            >
              <MoveOutUnit
                :customer-id="unitData.customer"
                :facility="facility"
                @completed="closeCompleteLeaseDialog"
              />
            </el-dialog>
            <el-popconfirm
              title="This will delete the Lease and delete unit rental(s) attached to it. Are you sure?"
              icon="el-icon-info"
              icon-color="red"
              confirm-button-type="danger"
              v-if="unitRentals[0] && unitRentals[0].status === 'PENDING'"
              @confirm="() => cancleLease(unitRentals[0].unitInvoice)"
            >
              <div>
                This will delete the Lease and revert the overdue balance.
              </div>
              <el-button slot="reference" icon="el-icon-close" type="danger" plain size="small">Cancel Lease</el-button>
            </el-popconfirm>
            <el-tooltip class="item" effect="light" content="Add move out date to its leasing." placement="top-start">
              <el-button
                v-if="unitRentals[0] && !unitRentals[0].moveOut && unitRentals[0].status !== statuses[8]"
                type="danger"
                size="small"
                plain
                @click="openMoveoutForm"
              >
                Set Moveout Date
              </el-button>
            </el-tooltip>
          </template>
        </el-descriptions>
        <el-descriptions v-if="unitData" title="Unit Details" direction="vertical" border :column="4">
          <el-descriptions-item label="Name" labelClassName="my-label">
            {{ unitData.name }}
          </el-descriptions-item>
          <el-descriptions-item label="Tier Quality" labelClassName="my-label">
            {{ tierQuality }}
          </el-descriptions-item>
          <el-descriptions-item v-if="!unitData.retired && unitData.status !== 'AVAILABLE' && unitData.status !== 'UNAVAILABLE'" label="Finance" labelClassName="my-label">
            <span>
              {{ unitData.leaseStatus | printableStatus }}
            </span>
          </el-descriptions-item>
          <el-descriptions-item v-if="!unitData.retired" label="Status" labelClassName="my-label">
            <span v-if="unitData.status !== 'AVAILABLE' && unitData.status !== 'UNAVAILABLE'">
              {{ unitData.status | printableStatus }}
            </span>
            <el-select
              v-else
              class="status"
              :value="unitData.status"
              size="small"
              @change="changeUnitStatus"
            >
              <el-option value="UNAVAILABLE" label="UNAVAILABLE">
              </el-option>
              <el-option value="AVAILABLE" label="AVAILABLE">
              </el-option>
            </el-select>
          </el-descriptions-item>
          <el-descriptions-item label="Description" labelClassName="my-label">
            {{ unitData.description }}
          </el-descriptions-item>
          <el-descriptions-item label="Type" labelClassName="my-label">
            {{ unitData.type }}
          </el-descriptions-item>
          <el-descriptions-item label="Asking Price" labelClassName="my-label">
            {{ price | currencySign($root.state.facilityData) }}
            <span v-if="securityFeeEnabled">
              + {{ computeSecurityFee(unitData.computedSize) | currencySign($root.state.facilityData) }}
            </span>
          </el-descriptions-item>
          <el-descriptions-item v-if="unitData.computedSize" label="Size" labelClassName="my-label">
            {{ unitData.computedSize }} Sq ft.
          </el-descriptions-item>
          <el-descriptions-item v-if="unitData.type !== 'Parking'" label="Dimensions (L x W x H)" labelClassName="my-label">
            {{ unitData.length }} x {{ unitData.width}} x {{ unitData.height }} ft.
          </el-descriptions-item>
          <el-descriptions-item v-if="unit && unitData.type !== 'Parking'" label="Size Tier" labelClassName="my-label">
            {{ sizeTier }}
          </el-descriptions-item>
          <el-descriptions-item v-if="unitData.tags && unitData.tags.length > 0" label="Tags" contentStyle="width: 25rem" labelClassName="my-label">
            <div class="tags-wrapper">
              <el-tag :key="tag.value" v-for="tag in unitData.tags" disable-transitions>
                {{tag.value}}
              </el-tag>
            </div>
          </el-descriptions-item>
        </el-descriptions>
        <br />
        <el-card v-loading="loading || settingPrimary" shadow="never" v-if="activeImage">
          <div slot="header">
            <el-button
              v-if="!activeImage.primary && unitData.images.length > 1"
              type="secondary"
              plain
              size="small"
              :loading="settingPrimary"
              @click="setAsPrimaryImage"
            >
              Set as primary
            </el-button>
            <el-tag type="success" v-else>Primary</el-tag>
            <el-button
              v-show="activeImage"
              @click="() => deleteImage(activeIndex)"
              type="danger"
              icon="el-icon-delete"
              circle
              plain
              style="float: center; margin-bottom: 1rem"
            ></el-button>
          </div>
          <el-carousel
            v-if="activeImage && !settingPrimary"
            @change="setActiveIndex"
            :autoplay="false"
            arrow="always"
            indicator-position="outside"
            height="30rem"
            trigger="click"
          >
            <el-carousel-item v-for="image in unitData.images" :key="image._id">
                <el-image style="height: 100%" :src="image.url"></el-image>
            </el-carousel-item>
          </el-carousel>
        </el-card>
      </el-tab-pane>
      <el-tab-pane label="Leasing History" name="leasing">
        <el-table
          :data="unitRentals"
          v-loading="unitRentalsLoading"
          @row-click="routeToCustomer"
        >
          <el-table-column
            label="Customer"
          >
            <template slot-scope="scope">
              {{ getCustomerEmail(scope.row.customer) }}
            </template>
          </el-table-column>
          <el-table-column label="Moved In">
            <template slot-scope="scope">
              <span>
                {{ scope.row.moveIn | formatDate }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="Moved Out">
            <template slot-scope="scope">
              <span v-if="scope.row.moveOut">
                {{ scope.row.moveOut | formatDate }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="Leased for">
            <template slot-scope="scope">
              <span v-if="scope.row.updatedPrice">
                {{ scope.row.updatedPrice | currencySign }}
              </span>
              <span v-else-if="scope.row.pricing">
                {{ scope.row.pricing.price | currencySign }}
              </span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="Currency" class="currency">
            <template slot-scope="scope" v-if="scope.row.pricing">
                {{ scope.row.pricing.currency.toUpperCase() }}
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
      <el-tab-pane label="Email Photos" name="email">
        <el-form :model="emailForm" ref="emailForm" v-loading="loading">
          <el-form-item label="Name"
            prop="name"
            :rules="[
             {
               required: true,
               message: 'Please input name',
               trigger: 'blur',
             }
           ]"
         >
           <el-input v-model="emailForm.name" />
         </el-form-item>
          <el-form-item label="Email"
            prop="email"
            :rules="[
             {
               required: true,
               message: 'Please input email address',
               trigger: 'blur',
             },
             {
               type: 'email',
               message: 'Please input correct email address',
               trigger: ['blur', 'change'],
             },
           ]"
         >
           <el-input v-model="emailForm.email" />
         </el-form-item>
         <el-form-item label="Send Floor Plan">
           <el-checkbox v-model="sendFloorPlan" :disabled="!Boolean(hasFloorPlan)"></el-checkbox>
         </el-form-item>
         <el-form-item>
            <el-button type="primary" :disabled="!emailForm.email" @click="sendEmail">
              Send
            </el-button>
          </el-form-item>
        </el-form>
      </el-tab-pane>
    </el-tabs>
    <el-dialog id="rental" :visible.sync="dialogFormVisible" destroy-on-close width="75%">
      <!-- <UnitRental /> -->
      <CustomerRental
        :facility="facility"
        :customer-id="selectedCustomer"
        :unit="unit"
        unit-type
        @customer-selected="customerSelected"
        @close="closeRentalDialog"
      />
    </el-dialog>
    <el-dialog
      v-if="unitData"
      :title="unitData.name" :visible.sync="dialogEditFormVisible" :close-on-click-modal="false">
        <NewSiteUnitType
          @unit-added="unitEditted"
          :unit="unitData"
        />
    </el-dialog>
    <el-dialog
      v-if="unitData"
      title="Update description" :visible.sync="dialogEditDescriptionFormVisible" :close-on-click-modal="false">
      <el-card shadow="never">
        <el-input v-model="description" />
        <el-button :disabled="description === unitData.description" style="margin-top: 1rem" :loading="updateItemLoading" @click="updateDescription">Save</el-button>
      </el-card>
    </el-dialog>
    <el-dialog
      v-if="unitData"
      title="Update tags" :visible.sync="dialogEditTagsFormVisible" :close-on-click-modal="false">
      <el-card shadow="never">
        <el-select v-model="tags" multiple>
          <el-option v-for="tag of tagsBasedOnType" :key="tag" :label="tag" :value="tag">
          </el-option>
        </el-select>
        <el-divider></el-divider>
        <div class="tags">
          <span>
            Previous Tags:
          </span>
          <el-tag class="tag" :key="tag.value" v-for="tag in unitData.tags">
            {{ tag.value }}
          </el-tag>
        </div>
        <el-divider></el-divider>
        <el-button :loading="updateItemLoading" @click="updateTags">Save</el-button>
      </el-card>
    </el-dialog>
    <el-dialog v-if="unitData" title="Add Images" :visible.sync="dialogEditImagesFormVisible" :close-on-click-modal="false" destroy-on-close>
      <edit-unit-images
        @images-added="updateImages"
      />
    </el-dialog>
    <el-dialog :visible.sync="dialogFormMoveout" modal>
      <el-card>
        <el-alert
          v-if="invoice && invoice.units.length === 1 && invoice.units[0] === unit"
          type="warning"
          :closable="false"
          description="The lease holds this single unit, adding move out date will add the end date for the leasing."
          show-icon>
        </el-alert>
        <el-alert
          v-else-if="!invoice || invoice.units.length === 0"
          type="warning"
          :closable="false"
          description="This rental has been created at the dev time, please ask the support team to attach the lease agreement."
          show-icon>
        </el-alert>
        <el-alert
          v-else-if="invoice && invoice.units.length > 1"
          type="warning"
          :closable="false"
          description="This lease holds multiple units, adding move out will add the same move out dates to other units."
          show-icon>
        </el-alert>
        <el-alert
          type="info"
          :closable="false"
          description="Make sure that the end date is set according to the agreement."
          show-icon>
        </el-alert>

        <br/>
        <el-date-picker
          v-model="endDate"
          type="date"
          :picker-options="datePickerOptions"
          placeholder="Move Out Date">
        </el-date-picker>
        <el-button :disabled="!endDate" @click="addEndDateToInvoice">
          Add
        </el-button>
      </el-card>
    </el-dialog>
    <el-dialog :visible.sync="dialogRetireFormVisible">
      <el-card shadow="never" v-loading="loading">
        <template slot="header">
          Retire Unit
        </template>
        <el-form :form="retireForm">
          <el-form-item label="Reason" prop="retirementReason" rules="[{ required: true, message: 'Please select a retirement reason' }]">
            <el-select v-model="retireForm.retirementReason">
              <el-option
                v-for="item in retirementReasons"
                :key="item.value"
                :label="item.label"
                :value="item.value">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="danger" :disabled="!retireForm.retirementReason" @click="retireUnit">
              Retire Now
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </el-dialog>
  </el-card>
</template>

<script>
import axios from 'axios';
import { UNITS_API_BASE, ADMIN_API_BASE, UNIT_RENTAL_API_BASE, CUSTOMER_API_BASE, LEASING_API, EMAIL_API_BASE } from '../env';
import CustomerRental from './CustomerRental.vue';
import NewSiteUnitType from './NewSiteUnitType.vue';
import EditUnitImages from './EditUnitImages.vue';
import MoveOutUnit from './MoveOutUnit.vue';
import statuses from '../utils/status';
import { getMatrixValue } from '../utils/sizeMatrix';
import { unitEmailV2Template } from '../utils/emailTemplates';
import { computeUnitPricing, round } from '../utils/pricing';
import unitTags, { officeTags, parkingTags } from '../utils/unitTags';

export default {
  name: 'Unit',
  components: { NewSiteUnitType, CustomerRental, MoveOutUnit, EditUnitImages },
  props: {
    facility: { type: String, required: true },
    unit: { type: String, required: true },
  },
  computed: {
    facilitySecurityFee() {
      const facilityData = this.facilityData;
      if (facilityData && facilityData.securityFee && facilityData.securityFee.enabled && facilityData.securityFee.fee > 0) {
        const computedSecurityFee = facilityData.securityFee.fee * this.unitData.computedSize / 100;
        const label = facilityData.securityFee.label || 'Security';

        return {
          fee: computedSecurityFee,
          label,
        };
      }

      return null;
    },
    hasFloorPlan() {
      const facilityData = this.facilityData;
      const officialDocs = facilityData ? facilityData.officialDocs : [];
      return [...officialDocs].find(doc => doc.name === 'Floor Plan');
    },
    securityFeeEnabled() {
      return this.facilityData.securityFee && this.facilityData.securityFee.enabled && this.facilityData.securityFee.fee > 0;
    },
    size() {
      return `${this.unitData.length * this.unitData.width} Sq ft.`;
    },
    hasNoPhotosTag() {
      const tags = this.unitData.tags.map(tag => tag.value);
      return tags.includes('No Photos');
    },
    sizeTier() {
      return getMatrixValue(this.unitData.size, this.unitData.type);
    },
    tierQuality() {
      if (this.unitData.tierQuality === 'AA') {
        return 'A+';
      }
      return this.unitData.tierQuality;
    },
    price() {
      if (this.unitData && this.facilityData) {
        return computeUnitPricing(this.unitData, this.facilityData);
      }
      return null;
    },
    tagsBasedOnType() {
      if (this.unitData.type === 'Office') {
        return officeTags;
      }

      if (this.unitData.type === 'Parking') {
        return parkingTags;
      }

      return unitTags;
    },
  },
  watch: {
    facility(value) {
      this.$router.push({ name: 'Facility Units', params: { facility: value } });
    },
    async unit() {
      try {
        this.loading = true;
        this.facilityData = await this.getFacility();
        this.unitData = await this.getUnit();
        if (this.unitData.images.length) {
          this.activeImage = { ...this.unitData.images[0] };
        }
        this.unitRentals = await this.getUnitRentals();
        this.customerIds = this.unitRentals.map(unit => unit.customer).filter((item) => {
          if (item) {
            return item;
          }
        });
        await this.getUnitInvoice();
        await this.getCustomers();
        this.loading = false;
      } catch (err) {
        this.loading = false;
      }
    },
  },
  async mounted() {
    try {
      this.loading = true;

      this.facilityData = await this.getFacility();
      this.unitData = await this.getUnit();
      this.tags = this.unitData.tags.map(tag => tag.value).filter(tag => tag !== 'No Photos');
      this.description = this.unitData.description || '';
      if (this.unitData.images.length) {
        this.activeImage = { ...this.unitData.images[0] };
      }
      this.unitRentals = await this.getUnitRentals();
      this.customerIds = this.unitRentals.map(unit => unit.customer).filter((item) => {
        if (item) {
          return item;
        }
      });
      await this.getCustomers();
      await this.getUnitInvoice();
      this.loading = false;
    } catch (err) {
      this.loading = false;
      this.$notification.error({
        title: 'Error',
        message: err.message,
      });
    }
  },
  data() {
    const endLeaseDateValidator = (date) => {
      if (new Date(this.invoice.start) > new Date()) {
        const moveIn = new Date(new Date(this.invoice.start).getTime());
        moveIn.setDate(moveIn.getDate() + 1);
        return date < moveIn;
      }
      return date < new Date();
    };
    return {
      description: '',
      activeIndex: 0,
      updateItemLoading: false,
      activeImage: null,
      settingPrimary: false,
      statuses,
      // Merged Units, Split Units, Test Unit, or Other
      retirementReasons: [{
        label: 'Merged Units',
        value: 'MERGED',
      }, {
        label: 'Split Units',
        value: 'SPLIT_UNITS',
      }, {
        label: 'Test Unit',
        value: 'TEST_UNITS',
      }, {
        label: 'Other',
        value: 'OTHER',
      }],
      retireForm: {
        retirementReason: '',
        retiredAt: new Date(),
        retiredBy: '',
      },
      emailForm: {
        email: '',
        name: '',
      },
      sendFloorPlan: false,
      unitData: null,
      selectedCustomer: null,
      completeLeaseDialog: false,
      unitRentals: [],
      loading: true,
      customerIds: [],
      customers: [],
      activeTab: 'info',
      unitRentalsLoading: true,
      error: '',
      dialogFormVisible: false,
      dialogRetireFormVisible: false,
      dialogEditImagesFormVisible: false,
      dialogEditTagsFormVisible: false,
      dialogEditDescriptionFormVisible: false,
      dialogEditFormVisible: false,
      dialogFormMoveout: false,
      facilityData: null,
      datePickerOptions: {
        disabledDate: endLeaseDateValidator,
      },
      endDate: null,
      invoice: null,
      tags: [],
    };
  },
  methods: {
    async setAsPrimaryImage() {
      this.settingPrimary = true;
      const images = [];
      this.unitData.images.forEach((image) => {
        if (image._id === this.activeImage._id) {
          image.primary = true;
          images.unshift(image);
        } else {
          image.primary = false;
          images.push(image);
        }
      });

      try {
        const response = await axios.put(`${UNITS_API_BASE}/unit/${this.unit}`, {
          images,
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.unitData = response.data;
        const index = [...this.unitData.images].findIndex(item => item._id === this.activeImage._id);
        this.setActiveIndex(index);
        this.settingPrimary = false;
      } catch (e) {
        this.settingPrimary = false;
        this.$message.error({
          title: 'Error',
          message: 'There was an error updating the images',
        });
      }

    },
    computeSecurityFee(size) {
      if (this.securityFeeEnabled) {
        return round((this.facilityData.securityFee.fee / 100) * size);
      }
      return 0;
    },
    setActiveIndex(newIndex) {
      this.activeIndex = newIndex;
      this.activeImage = { ...this.unitData.images[newIndex] };
    },
    async retireUnit() {
      this.loading = true;
      try {
        await axios.patch(`${UNITS_API_BASE}/unit/${this.unit}`, {
          retiredBy: this.$keycloak.tokenParsed.email,
          retirementReason: this.retireForm.retirementReason,
          retiredAt: new Date(),
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.response.data,
        });
      }

      this.unitData = await this.getUnit();
      this.loading = false;
      this.dialogRetireFormVisible = false;
    },
    async deleteImage(index) {
      this.unitData.images.splice(index, 1);
      await this.updateImages([]);
      if (this.unitData.images && !this.unitData.images.length) {
        this.activeImage = null;
      }
    },
    async cancleLease(invoice) {
      try {
        this.loading = true;
        await axios.patch(
          `${LEASING_API}/v1/${this.facility}/${invoice}/cancel`,
          {},
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          });
        await this.getCustomerInvoices();
        this.$notify.success({
          title: 'Lease Cancelled',
          message: 'Success!',
        });
        await this.reloadState();
        this.loading = false;
      } catch (err) {
        this.$notify.error({
          title: 'Error',
          message: err && err.response ? err.response.data : err,
        });
      }
    },
    customerSelected(customer) {
      this.selectedCustomer = customer;
    },
    async reloadState() {
      this.facilityData = await this.getFacility();
      this.unitData = await this.getUnit();
      this.unitRentals = await this.getUnitRentals();
      this.customerIds = this.unitRentals.map(unit => unit.customer).filter((item) => {
        if (item) {
          return item;
        }
      });
      await this.getCustomers();
      await this.getUnitInvoice();
    },
    async closeRentalDialog() {
      this.dialogFormVisible = false;
      this.loading = true;
      this.facilityData = await this.getFacility();
      this.unitData = await this.getUnit();
      this.unitRentals = await this.getUnitRentals();
      this.customerIds = this.unitRentals.map(unit => unit.customer).filter((item) => {
        if (item) {
          return item;
        }
      });
      await this.getCustomers();
      await this.getUnitInvoice();
      this.loading = false;
    },
    async addEndDateToInvoice() {
      try {
        this.loading = true;
        await axios.patch(
          `${LEASING_API}/v1/${this.facility}/${this.invoice._id}/start/lease/termination`,
          {
            end: this.endDate,
          },
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );

        this.dialogFormMoveout = false;
        this.$notify.success({
          title: 'Success',
          message: 'End date added successfully.',
        });
        await this.reloadState();

        this.loading = false;
      } catch (err) {
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async getUnitInvoice() {
      if (!this.unitData.invoice) {
        return;
      }
      const response = await axios.get(`${CUSTOMER_API_BASE}/customers/v1/customer/invoice/${this.unitData.invoice}`, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      this.invoice = response.data;
    },
    async getUnitRentals() {
      this.unitRentalsLoading = true;
      const response = await axios.get(`${UNIT_RENTAL_API_BASE}`, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
        params: {
          unit: this.unit,
          customer: this.unit.customer,
          unitInvoice: this.unit.invoice,
        },
      });
      this.unitRentalsLoading = false;
      return response.data.result;
    },
    getCustomerEmail(id) {
      if (!id) {
        return '-';
      }
      const filteredCustomer = this.customers.filter((customer) => {
        if (customer._id === id) {
          return customer;
        }
      });
      if (filteredCustomer.length > 0) {
        return filteredCustomer[0].email;
      }
      return '-';
    },
    async getCustomers() {
      try {
        this.loading = true;
        const response = await axios.get(`${CUSTOMER_API_BASE}/customers/v1/get/all`, {
          params: {
            customers: this.customerIds.toString(),
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.loading = false;
        // eslint-disable-next-line no-console
        this.customers = response.data;
      } catch (err) {
        this.loading = false;
      }
    },
    async getFacility() {
      const response = await axios.get(`${ADMIN_API_BASE}/facilities/${this.facility}`, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      return response.data;
    },
    async getUnit() {
      try {
        const response = await axios.get(`${UNITS_API_BASE}/unit/${this.unit}`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        return response.data;
      } catch (err) {
        if (err.response && err.response.data) {
          this.$message.error({
            title: 'Error',
            message: err.response.data,
          });

          if (err.response.data === 'access_denied') {
            this.$router.push({ name: '404' });
          }
        }
        throw new Error(err.response.data);
      }
    },
    async sendEmail() {
      this.loading = true;
      try {
        const tags = this.unitData.tags.map(item => item.value).filter(tag => tag !== 'No Photos');
        if (this.unitData.images.length > 0) {
          // const mjmlTemplate = emailPhotosTemplate(
          //   this.facilityData.title,
          //   this.facilityData.addressInfo,
          //   this.unitData,
          //   this.facilityData.manager,
          //   tags.join(', '),
          //   this.price,
          //   this.facilityData.pricing.currency,
          //   this.facilitySecurityFee,
          // );
          const mjmlTemplate = unitEmailV2Template(
            this.emailForm.name,
            this.facilityData.title,
            this.facilityData.addressInfo,
            this.unitData,
            { ...this.facilityData.manager, ...this.$keycloak.tokenParsed },
            tags,
            this.price,
            this.facilityData.pricing.currency,
            this.facilitySecurityFee,
            this.facilityData.rentTaxFlag,
            this.facilityData.tax ? this.facilityData.tax.rentTaxLabel : null,
          );
          await axios.post(`${EMAIL_API_BASE}/send/email`, {
            from: this.$keycloak.tokenParsed.email,
            to: this.emailForm.email,
            cc: this.$keycloak.tokenParsed.email,
            subject: 'Requested ReadySpaces Unit Photos',
            facility: this.facilityData._id,
            mjml: true,
            body: mjmlTemplate,
            attachments: [...(this.hasFloorPlan && this.sendFloorPlan ? [{
              name: 'FloorPlan.pdf',
              type: 'application/pdf',
              url: this.hasFloorPlan.url,
            }] : [])],
          }, {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          });

          this.emailForm.email = '';
          this.emailForm.name = '';
          this.$message.success({
            title: 'Email sent',
            message: 'Email sent successfully',
          });
          this.loading = false;
        } else {
          this.loading = false;
          this.$message.warning({
            title: 'Images Does not exist',
            message: 'This unit does not have any images',
          });
        }
      } catch (err) {
        this.loading = false;
        this.$message.error({
          title: 'Error',
          message: 'Send email failed',
        });
      }
    },
    openRentForm() {
      this.dialogFormVisible = true;
    },
    openRetireUnitForm() {
      this.dialogRetireFormVisible = true;
    },
    openMoveoutForm() {
      this.dialogFormMoveout = true;
    },
    routeToHomePage() {
      // this.$router.push({ name: 'Facility Units', params: { facility: this.facility } });
      this.$router.back();
    },
    openEditImagesDialog() {
      this.dialogEditImagesFormVisible = true;
    },
    openEditDescriptionDialog() {
      this.description = this.unitData.description;
      this.dialogEditDescriptionFormVisible = true;
    },
    openEditTagsDialog() {
      this.dialogEditTagsFormVisible = true;
    },
    editUnit() {
      this.dialogEditFormVisible = true;
    },
    async changeUnitStatus(status) {
      this.loading = true;
      try {
        const updatedUnit = await axios.put(`${UNITS_API_BASE}/unit/status/${this.unit}`, {
          status,
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });

        this.unitData = updatedUnit.data;

        this.loading = false;
        this.$notify.success({
          title: 'Status changed',
          message: `To ${status} successfully.`,
        });
      } catch (err) {
        this.loading = false;
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async updateImages(images) {
      this.loading = true;
      try {
        const response = await axios.put(`${UNITS_API_BASE}/unit/${this.unit}`, {
          images: [...this.unitData.images, ...images],
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.unitData = response.data;
        this.setActiveIndex(0);
        this.dialogEditImagesFormVisible = false;
      } catch (e) {
        this.$message.error({
          title: 'Error',
          message: 'There was an error updating the images',
        });
      }
      this.loading = false;
    },
    async updateDescription() {
      this.loading = true;
      try {
        this.updateItemLoading = true;
        const response = await axios.put(`${UNITS_API_BASE}/unit/${this.unit}`, {
          description: this.description,
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.unitData = response.data;

        this.updateItemLoading = false;
        this.dialogEditDescriptionFormVisible = false;
        this.$message.success({
          message: 'Updated description successfully!',
        });
      } catch (e) {
        this.$message.error({
          title: 'Error',
          message: 'There was an error updating the description',
        });
      }
      this.loading = false;
    },
    async updateTags() {
      this.loading = true;
      try {
        this.updateItemLoading = true;
        const response = await axios.put(`${UNITS_API_BASE}/unit/${this.unit}`, {
          tags: [...this.tags.map(tag => ({ value: tag })), ...(this.hasNoPhotosTag && this.unitData.type !== 'Parking' ? [{ value: 'No Photos' }] : [])],
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.updateItemLoading = false;
        this.unitData = response.data;
        this.dialogEditTagsFormVisible = false;
        this.$message.success({
          message: 'Updated tags successfully!',
        });
      } catch (e) {
        this.$message.error({
          title: 'Error',
          message: 'There was an error updating the tags',
        });
      }
      this.loading = false;
    },
    async unitEditted(edittedUnit) {
      this.dialogEditFormVisible = false;
      this.loading = true;
      try {
        const response = await axios.put(`${UNITS_API_BASE}/unit/${this.unit}`, {
          ...edittedUnit,
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.unitData = response.data;
        this.loading = false;
        this.$notify.success({ title: 'Success', message: 'Unit updated successfully.' });
      } catch (err) {
        this.loading = false;
        this.$notify.error({ title: 'Error', message: err.response.data });
      }
    },
    routeToCustomer(row) {
      if (this.facility) {
        this.$router.push({ name: 'Facility Customer', params: { id: row.customer, facility: this.facility } });
        return;
      }
      this.$router.push({ name: 'Customer', params: { id: row.customer } });
    },
    routeToUnit(row) {
      // eslint-disable-next-line no-underscore-dangle
      this.$router.push({ name: 'Unit', params: { unit: row._id, facility: this.facility } });
    },
    openCompleteLeaseDialog() {
      this.completeLeaseDialog = true;
    },
    async closeCompleteLeaseDialog() {
      this.loading = true;
      this.completeLeaseDialog = false;
      await this.reloadState();
      this.loading = false;
    },
    checkForPhotos(tab) {
      if (tab.name === 'email' && this.unitData && !this.unitData.images.length) {
        this.$message.warning({
          title: 'Warning',
          message: 'Please add photos for the unit to send email.',
        });
      }
    },
  },
};
</script>

<style>
.tags-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-content: stretch;
}

.my-label {
  background: #009aca !important;
  color: white !important;
}

.tag {
  margin-right: 8px;
}

.tags {
  margin-top: 8px;
}

.el-carousel__arrow {
  background: white;
  border: solid 2px deepskyblue;
  font-size: 20px;
  color: deepskyblue;
}
.el-carousel__arrow:hover {
  background: deepskyblue;
  color: white;
}
.el-carousel__button {
  height: 30px;
  border-radius: 5px;
}
</style>



// WEBPACK FOOTER //
// src/components/Unit.vue